import type { ActivityDTO } from 'src/types/dto';
import type { ActivityDependencyCriterion } from 'src/types/dto/activity.dto';

import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid,
  Stack,
  IconButton,
  TextField,
  Typography,
  Button,
  MenuItem,
} from '@mui/material';

import axios, { endpoints } from 'src/utils/axios';
import { addingPrerequisiteCreatesCycle } from 'src/utils/activity-dependency-cycle';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import {
  VirtualizedAutocomplete,
  Option,
} from 'src/components/virtualized-autocomplete';
import {
  ActivityDependencyBoundType,
  ActivityDependencyDirection,
  ActivityDependencyDurationType,
} from 'src/types/dto/activity.dto';

export type ActivityDependencyFormValue = {
  dependsOnActivityId: number;
  criteria: ActivityDependencyCriterion[];
};

const emptyCriterion = (): ActivityDependencyCriterion => ({
  type: ActivityDependencyBoundType.MAX,
  direction: ActivityDependencyDirection.AFTER,
  duration: 0,
  durationType: ActivityDependencyDurationType.DAY,
});

const formatDependencyActivityLabel = (
  activity: Pick<ActivityDTO, 'name'> & { site?: ActivityDTO['site'] },
  noSiteLabel: string
) => {
  const siteName = activity.site?.name?.trim();
  return siteName ? `${siteName} – ${activity.name}` : `${noSiteLabel} – ${activity.name}`;
};

type Props = {
  projectId?: number;
  currentActivityId?: number;
  value: ActivityDependencyFormValue[];
  onChange: (value: ActivityDependencyFormValue[]) => void;
  canEdit: boolean;
};

export function ActivityDependenciesField({
  projectId,
  currentActivityId,
  value,
  onChange,
  canEdit,
}: Props) {
  const { t } = useTranslation('activities');
  const [projectActivities, setProjectActivities] = useState<ActivityDTO[]>([]);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [pickerActivity, setPickerActivity] = useState<Option | null>(null);
  const [draftCriterion, setDraftCriterion] = useState<ActivityDependencyCriterion>(emptyCriterion());

  useEffect(() => {
    if (!canEdit || !projectId) {
      setProjectActivities([]);
      return;
    }
    let cancelled = false;
    axios
      .get<ActivityDTO[]>(endpoints.activities.byProject(String(projectId)))
      .then((res) => {
        if (!cancelled) {
          setProjectActivities(
            (res.data ?? []).filter((a) => a.id !== currentActivityId)
          );
        }
      })
      .catch(() => {
        if (!cancelled) setProjectActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canEdit, projectId, currentActivityId]);

  const selectedIds = useMemo(() => new Set(value.map((v) => v.dependsOnActivityId)), [value]);

  const pickerOptions: Option[] = useMemo(() => {
    const noSite = t('form.details.dependencies.noSite');
    return projectActivities
      .filter((a) => !selectedIds.has(a.id))
      .filter((a) => {
        if (!currentActivityId) return true;
        return !addingPrerequisiteCreatesCycle(
          currentActivityId,
          a.id,
          value,
          projectActivities
        );
      })
      .map((activity) => ({
        key: activity.id,
        label: formatDependencyActivityLabel(activity, noSite),
      }))
      .filter((o) => {
        if (!searchText?.trim()) return true;
        return o.label.toLowerCase().includes(searchText.toLowerCase());
      });
  }, [projectActivities, selectedIds, searchText, t, currentActivityId, value]);

  const resolveLabel = (dep: ActivityDependencyFormValue) => {
    const fromProject = projectActivities.find((a) => a.id === dep.dependsOnActivityId);
    if (fromProject) {
      return formatDependencyActivityLabel(fromProject, t('form.details.dependencies.noSite'));
    }
    return `#${dep.dependsOnActivityId}`;
  };

  const handleAdd = () => {
    if (!pickerActivity || !currentActivityId) return;
    const id = Number(pickerActivity.key);
    if (
      addingPrerequisiteCreatesCycle(
        currentActivityId,
        id,
        value,
        projectActivities
      )
    ) {
      toast.error(t('common:feedbackCodes.ACTIVITY_DEPENDENCY_CYCLE'));
      return;
    }
    const criteria =
      draftCriterion.duration > 0
        ? [{ ...draftCriterion, duration: Math.max(0, Math.floor(draftCriterion.duration) || 0) }]
        : [];
    onChange([
      ...value,
      { dependsOnActivityId: id, criteria },
    ]);
    setPickerActivity(null);
    setDraftCriterion(emptyCriterion());
    setSearchText(null);
  };

  const handleRemove = (dependsOnActivityId: number) => {
    onChange(value.filter((v) => v.dependsOnActivityId !== dependsOnActivityId));
  };

  const updateCriteria = (
    dependsOnActivityId: number,
    criteria: ActivityDependencyCriterion[]
  ) => {
    onChange(
      value.map((v) =>
        v.dependsOnActivityId === dependsOnActivityId ? { ...v, criteria } : v
      )
    );
  };

  const handleCriterionChange = (
    dependsOnActivityId: number,
    index: number,
    patch: Partial<ActivityDependencyCriterion>
  ) => {
    const dep = value.find((v) => v.dependsOnActivityId === dependsOnActivityId);
    if (!dep) return;
    const criteria = dep.criteria.map((c, i) => (i === index ? { ...c, ...patch } : c));
    updateCriteria(dependsOnActivityId, criteria);
  };

  const handleAddCriterion = (dependsOnActivityId: number) => {
    const dep = value.find((v) => v.dependsOnActivityId === dependsOnActivityId);
    if (!dep) return;
    updateCriteria(dependsOnActivityId, [...dep.criteria, emptyCriterion()]);
  };

  const handleRemoveCriterion = (dependsOnActivityId: number, index: number) => {
    const dep = value.find((v) => v.dependsOnActivityId === dependsOnActivityId);
    if (!dep) return;
    updateCriteria(
      dependsOnActivityId,
      dep.criteria.filter((_, i) => i !== index)
    );
  };

  if (!canEdit) return null;

  const boundTypeOptions = [
    { value: ActivityDependencyBoundType.MIN, label: t('form.details.dependencies.boundType.min') },
    { value: ActivityDependencyBoundType.MAX, label: t('form.details.dependencies.boundType.max') },
  ];
  const directionOptions = [
    {
      value: ActivityDependencyDirection.BEFORE,
      label: t('form.details.dependencies.direction.before'),
    },
    {
      value: ActivityDependencyDirection.AFTER,
      label: t('form.details.dependencies.direction.after'),
    },
  ];
  const durationTypeOptions = [
    {
      value: ActivityDependencyDurationType.DAY,
      label: t('form.details.dependencies.durationType.day'),
    },
    {
      value: ActivityDependencyDurationType.WEEK,
      label: t('form.details.dependencies.durationType.week'),
    },
    {
      value: ActivityDependencyDurationType.MONTH,
      label: t('form.details.dependencies.durationType.month'),
    },
    {
      value: ActivityDependencyDurationType.YEAR,
      label: t('form.details.dependencies.durationType.year'),
    },
  ];

  const renderCriterionEditors = (
    criteria: ActivityDependencyCriterion[],
    onFieldChange: (index: number, patch: Partial<ActivityDependencyCriterion>) => void,
    onRemove: (index: number) => void,
    allowRemove: boolean
  ) =>
    criteria.map((criterion, index) => (
      <Stack
        key={index}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <TextField
          select
          size="small"
          label={t('form.details.dependencies.boundType.label')}
          value={criterion.type}
          onChange={(e) =>
            onFieldChange(index, { type: e.target.value as ActivityDependencyBoundType })
          }
          sx={{ minWidth: 110 }}
        >
          {boundTypeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="number"
          label={t('form.details.dependencies.duration')}
          value={criterion.duration}
          onChange={(e) =>
            onFieldChange(index, {
              duration: Math.max(0, parseInt(e.target.value, 10) || 0),
            })
          }
          inputProps={{ min: 0, step: 1 }}
          sx={{ width: { xs: '100%', sm: 100 } }}
        />
        <TextField
          select
          size="small"
          label={t('form.details.dependencies.durationType.label')}
          value={criterion.durationType}
          onChange={(e) =>
            onFieldChange(index, {
              durationType: e.target.value as ActivityDependencyDurationType,
            })
          }
          sx={{ minWidth: 110 }}
        >
          {durationTypeOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('form.details.dependencies.direction.label')}
          value={criterion.direction}
          onChange={(e) =>
            onFieldChange(index, {
              direction: e.target.value as ActivityDependencyDirection,
            })
          }
          sx={{ minWidth: 110 }}
        >
          {directionOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        {allowRemove && (
          <IconButton
            size="small"
            color="error"
            aria-label={t('form.details.dependencies.removeCriterion')}
            onClick={() => onRemove(index)}
          >
            <Iconify icon="solar:trash-bin-trash-bold" width={18} />
          </IconButton>
        )}
      </Stack>
    ));

  return (
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('form.details.dependencies.label')}
      </Typography>
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <VirtualizedAutocomplete
              label={t('form.details.dependencies.addActivity')}
              options={pickerOptions}
              selectedOption={pickerActivity}
              onChange={(opt) => setPickerActivity(opt ?? null)}
              onTextChange={setSearchText}
              textFieldProps={{
                placeholder: t('form.details.dependencies.placeholder'),
                size: 'small',
              }}
            />
          </Box>
          {renderCriterionEditors(
            [draftCriterion],
            (_index, patch) => setDraftCriterion((prev) => ({ ...prev, ...patch })),
            () => undefined,
            false
          )}
          <Button
            variant="outlined"
            size="medium"
            disabled={!pickerActivity}
            onClick={handleAdd}
            sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
          >
            {t('form.details.dependencies.add')}
          </Button>
        </Stack>

        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('form.details.dependencies.empty')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {value.map((dep) => (
              <Stack
                key={dep.dependsOnActivityId}
                spacing={1}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'background.neutral',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                    {resolveLabel(dep)}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => handleAddCriterion(dep.dependsOnActivityId)}
                  >
                    {t('form.details.dependencies.addCriterion')}
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={t('form.details.dependencies.remove')}
                    onClick={() => handleRemove(dep.dependsOnActivityId)}
                  >
                    <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                  </IconButton>
                </Stack>
                {dep.criteria.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('form.details.dependencies.orderOnly')}
                  </Typography>
                ) : (
                  renderCriterionEditors(
                    dep.criteria,
                    (index, patch) =>
                      handleCriterionChange(dep.dependsOnActivityId, index, patch),
                    (index) => handleRemoveCriterion(dep.dependsOnActivityId, index),
                    true
                  )
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Grid>
  );
}
